import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithCustomToken,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate Twitch OAuth URL
  function getTwitchAuthUrl() {
    const clientId = process.env.REACT_APP_TWITCH_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/twitch/callback`);
    const scopes = encodeURIComponent('user:read:email');
    const state = Math.random().toString(36).substring(2, 15);
    
    // Store state in localStorage for verification
    localStorage.setItem('twitch_oauth_state', state);
    
    return `https://id.twitch.tv/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}`;
  }

  // Handle Twitch OAuth callback
  async function handleTwitchCallback(code, state) {
    try {
      // Verify state parameter for security
      const storedState = localStorage.getItem('twitch_oauth_state');
      
      // Only verify state if both are present
      if (state && storedState && state !== storedState) {
        throw new Error('Invalid state parameter');
      }
      
      // Remove state from localStorage
      localStorage.removeItem('twitch_oauth_state');

      const redirectUri = `${window.location.origin}/auth/twitch/callback`;
      
      // Call Firebase Function to exchange code for token
      const response = await fetch(`${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/twitchAuth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, redirect_uri: redirectUri })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Authentication failed: ${response.status} ${response.statusText}`);
      }

      const { token, user } = await response.json();
      
      // Sign in with custom token
      await signInWithCustomToken(auth, token);
      
      return user;
    } catch (error) {
      throw error;
    }
  }

  // Start Twitch OAuth flow
  function loginWithTwitch() {
    const authUrl = getTwitchAuthUrl();
    window.location.href = authUrl;
  }

  // Logout function
  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loginWithTwitch,
    handleTwitchCallback,
    logout,
    getTwitchAuthUrl
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 